const BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080/api';

// Firebase storage URLs
const FIREBASE_STORAGE_BASE_URL_DEV = 'https://firebasestorage.googleapis.com/v0/b/stack-94b04.appspot.com/o/';
const FIREBASE_STORAGE_BASE_URL_PROD = 'https://firebasestorage.googleapis.com/v0/b/mystack-41374.appspot.com/o/';
const FIREBASE_STORAGE_BASE_URL = process.env.REACT_APP_ENVIRONMENT === 'production' ? FIREBASE_STORAGE_BASE_URL_PROD : FIREBASE_STORAGE_BASE_URL_DEV;

// Stack versioning
const STACK_VERSION = "1.2"; // Current stack version

// Supabase URL for Edge Functions
const SUPABASE_URL = process.env.REACT_APP_SUPABASE_URL;

// Edge Function URL based on environment
const getEdgeFunctionUrl = () => {
  return `${SUPABASE_URL}/functions/v1`;
};

const EDGE_FUNCTION_URL = getEdgeFunctionUrl();

const UNITS = {
  mg: 'mg',
  mcg: 'mcg',
  g: 'g',
  IU: 'IU',
  ml: 'ml',
  fl_oz: 'fl oz',
  tbsp: 'tbsp',
  tsp: 'tsp',
  mcg_DFE: 'mcg DFE',
  mg_NE: 'mg NE',
  mcg_RAE: 'mcg RAE',
  mg_alpha_tocopherol: 'mg alpha-tocopherol',
  billion_CFU: 'billion CFU',
  tablet: 'Tablet',
  capsule: 'Capsule' /*,
  chewable_tablet: 'Chewable tablet',
  capsule: 'Capsule',
  softgel: 'Softgel',
  gelcap: 'Gelcap',
  caplet: 'Caplet',
  gummy: 'Gummy',
  powder: 'Powder',
  lozenge: 'Lozenge',
  bar: 'Bar',*/
};

// Maximum number of stack generations for non-logged-in users.
// Effectively unlimited outside production to ease dev/staging testing —
// the backend's Supabase-backed rate limit still applies there.
const MAX_REQUESTS = process.env.REACT_APP_ENVIRONMENT === 'production' ? 2 : Infinity;

// Preset health goals for popular stacks
// This is the frontend source of truth - keep in sync with:
// - backend/config/preset_goals.py (backend validation)
// - StackCreationForm.js sampleGoals (form chips)
const PRESET_HEALTH_GOALS = {
  'weight-management': 'Weight Management',
  'muscle-health': 'Muscle Health',
  'digestion': 'Digestion',
  'energy': 'Energy',
  'sleep-hygiene': 'Sleep Hygiene',
  'heart-health': 'Heart Health',
  'focus': 'Focus',
  'stress-relief': 'Stress Relief',
  'immunity': 'Immunity',
  'healthy-hair-skin': 'Healthy Hair & Skin',
  'healthy-aging': 'Healthy Aging',
  'memory': 'Memory',
  'joint-health': 'Joint Health',
  'bone-health': 'Bone Health',
  'prostate-health': 'Prostate Health',
  'eye-health': 'Eye Health',
  'prenatal-postnatal': 'Prenatal & Postnatal',
  'healthy-mood': 'Healthy Mood',
  'glucose-metabolism': 'Glucose Metabolism',
  'hormone-balance': 'Hormone Balance'
};

// Feature flags
const FEATURES = {
  // Use Supabase-hosted blog posts instead of static markdown/blogData.js
  // Set to false to roll back to static files without any code change (just rebuild)
  USE_SUPABASE_BLOG: process.env.REACT_APP_USE_SUPABASE_BLOG === 'true',

  // The affiliate store (`/market`) is launched and on in every environment,
  // production included. Set to false only to take it down deliberately.
  // (The old note here said to switch it off before a production deploy —
  // stale since launch, and it read as an unintended production exposure.)
  AFFILIATE_STORE: true,

  // Logged-out visitors add medications in the stack composer and see the
  // interaction check on their free stack (STACK-565,
  // docs/guest-medication-check.md). The kill switch for the entry point:
  // false hides the composer's Medications control, so no visitor adds any
  // and the stack page never runs the guest check. Rollback is this line.
  GUEST_MEDICATION_CHECK: true,

  // The supplement interaction checker page, /supplement-interaction-checker
  // (STACK-587, docs/supplement-interaction-checker.md). The kill switch:
  // false keeps the page's heading, intro and disclaimer — it is indexed and
  // should not 404 — with no form and no requests. Rollback is this line.
  INTERACTION_CHECKER: true,
  
  // Set to true to use the parallel research stack implementation
  // This provides faster stack generation by processing article chunks in parallel
  USE_PARALLEL_RESEARCH_STACK: true, // Set to true to enable parallel processing

  // NEW Feature Flag for Async Stack Generation with Polling
  USE_ASYNC_STACK_GENERATION: false, // Set to true to enable async/polling, false for old sync method

  AI_ANALYSIS: process.env.REACT_APP_AI_ANALYSIS_ENABLED === 'true',

  PROFILE_REDESIGN: process.env.REACT_APP_FF_PROFILE_REDESIGN === 'true',

  // Research tab visibility - set to false to temporarily hide research tab on condition pages
  SHOW_RESEARCH_TAB_CONDITIONS: false,

  // Account deletion feature - ENABLED FOR STAGING TESTING
  // ✅ Implementation complete - see docs/account_deletion/
  // ✅ Cloud Function with subscription verification
  // ✅ Complete data deletion (all user collections)
  // ✅ Business records preserved (financial data)
  // ⚠️  STAGING ONLY - Test thoroughly before production
  ACCOUNT_DELETION_ENABLED: true,

  // Pro Feature Definitions - Single Source of Truth
  IS_PRO_FEATURE: {
    // Core Pro Features
    'research_stack': true,
    'medication_support': true,
    'diet_support': true,
    'advanced_analysis': true,
    'pdf_insights': true,

    // Future Pro Features (easy to add)
    'ai_personalization': true,
    'export_reports': true,
    'priority_support': true,
    
    // Free Features (explicitly marked for clarity)
    'basic_stack_generation': false,
    'save_stacks': false,
    'profile_management': false,
  }
};

const config = {
  BASE_URL,
  FIREBASE_STORAGE_BASE_URL,
  SUPABASE_URL,
  EDGE_FUNCTION_URL,
  UNITS,
  MAX_REQUESTS,
  FEATURES,
  PRESET_HEALTH_GOALS,
  STACK_VERSION, // Export the stack version
  GOOGLE_AUTH_CLIENT_ID: process.env.REACT_APP_GOOGLE_AUTH_CLIENT_ID,
  // Add other environment-specific configurations here
  FIREBASE_CONFIG: {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
    messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
    appId: process.env.REACT_APP_FIREBASE_APP_ID,
    measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  },
  API_BASE_URL: BASE_URL,
  // Stripe configuration for subscription system
  STRIPE_PRICE_ID: process.env.REACT_APP_STRIPE_PRICE_ID,
  STRIPE_MEMBERSHIP_PRODUCT_ID: process.env.REACT_APP_STRIPE_MEMBERSHIP_PRODUCT_ID,
  
  // Open Stripe checkout/portal in new tab for better compatibility (web & mobile webview)
  OPEN_STRIPE_IN_NEW_TAB: false,

  // App store links
  APP_STORE_IOS: 'https://apps.apple.com/us/app/mystackai/id6743498624',
  APP_STORE_ANDROID: 'https://play.google.com/store/apps/details?id=com.balam.mystack',
};

module.exports = config;
